import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Loader2,
    AlertTriangle,
    FileText,
    Calendar,
    ArrowUp,
    ArrowDown,
    ArrowUpDown,
    ChevronLeft,
    ChevronRight,
    Download,
} from 'lucide-react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import './AccuracyMonitoringPage.css';
import { useAccuracyMonitoringTimeseries, useAccuracyMonitoringJobs, useJobFilterOptions } from '../hooks/useApi';
import MultiSelectFilter from '../components/MultiSelectFilter';
import { buildMonitoringDrilldownPath, type MetricCountKind } from '../services/accuracyCountDrilldown';
import { useToast } from '../components/toast/ToastProvider';
import {
    accuracyMonitoringApi,
    violationsApi,
    type AccuracyMonitoringJobRow,
    type ViolationExportDownloadResponse,
    type ViolationExportProgressResponse,
} from '../services/api';

const ITEMS_PER_PAGE = 20;

const INACTIVE_LEGEND_COLOR = '#cbd5e1';

type SeriesConfig = { key: string; name: string; color: string; axis?: 'counts' | 'urls' };

const COUNT_SERIES: SeriesConfig[] = [
    { key: 'violations_after_review_count', name: 'レビュー後の違反数', color: '#22c55e', axis: 'counts' },
    { key: 'fp_count', name: '誤検知（FP）', color: '#ef4444', axis: 'counts' },
    { key: 'fn_count', name: '検知漏れ（FN）', color: '#f59e0b', axis: 'counts' },
    { key: 'wrong_bounding_box_count', name: '枠ずれ', color: '#6366f1', axis: 'counts' },
    { key: 'reviewed_url_count', name: 'レビュー済みURL数', color: '#a855f7', axis: 'urls' },
];

const RATE_SERIES: SeriesConfig[] = [
    { key: 'fp_rate', name: '誤検知率（FP）', color: '#ef4444' },
    { key: 'fn_rate', name: '検知漏れ率（FN）', color: '#f59e0b' },
    { key: 'wrong_bounding_box_rate', name: '枠ずれ率', color: '#6366f1' },
];

type DivideBy = 'none' | 'guideline' | 'category';

const DIVIDE_BY_OPTIONS: { value: DivideBy; label: string }[] = [
    { value: 'none', label: '内訳なし' },
    { value: 'guideline', label: 'ガイドライン別' },
    { value: 'category', label: '違反カテゴリ別' },
];

// Single-metric picker shown while the chart is divided into one line per
// group -- plotting every metric x every group at once would be unreadable.
const DIVIDED_COUNT_METRICS: { key: string; name: string }[] = [
    { key: 'violations_after_review_count', name: 'レビュー後の違反数' },
    { key: 'fp_count', name: '誤検知（FP）' },
    { key: 'fn_count', name: '検知漏れ（FN）' },
    { key: 'wrong_bounding_box_count', name: '枠ずれ' },
];
const GUIDELINE_ONLY_COUNT_METRIC = { key: 'reviewed_url_count', name: 'レビュー済みURL数' };

const DIVIDED_RATE_METRICS: { key: string; countKey: string; name: string }[] = [
    { key: 'fp_rate', countKey: 'fp_count', name: '誤検知率（FP）' },
    { key: 'fn_rate', countKey: 'fn_count', name: '検知漏れ率（FN）' },
    { key: 'wrong_bounding_box_rate', countKey: 'wrong_bounding_box_count', name: '枠ずれ率' },
];

// Cycled by group index when a chart is divided into more groups than colors.
const GROUP_PALETTE = [
    '#ef4444', '#3b82f6', '#22c55e', '#f59e0b', '#a855f7',
    '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
];

const BUCKET_OPTIONS: { value: 'day' | 'week' | 'month'; label: string }[] = [
    { value: 'day', label: '日別' },
    { value: 'week', label: '週別' },
    { value: 'month', label: '月別' },
];

const getPageNumbers = (current: number, total: number): (number | '...')[] => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (current > 3) pages.push('...');
    for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) pages.push(p);
    if (current < total - 2) pages.push('...');
    pages.push(total);
    return pages;
};

const formatDateForInput = (iso: string | undefined): string => (iso ? iso.slice(0, 10) : '');

interface SortableThProps {
    field: string;
    label: string;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    onSort: (field: string) => void;
}

const SortableTh: React.FC<SortableThProps> = ({ field, label, sortBy, sortOrder, onSort }) => {
    const isActive = sortBy === field;
    return (
        <th className="accuracy-monitoring-sortable-th" onClick={() => onSort(field)}>
            <span className="accuracy-monitoring-th-content">
                {label}
                {isActive ? (
                    sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                ) : (
                    <ArrowUpDown size={12} className="accuracy-monitoring-sort-icon-idle" />
                )}
            </span>
        </th>
    );
};

interface MonitoringCountCellProps {
    count: number;
    onOpen: () => void;
}

const MonitoringCountCell: React.FC<MonitoringCountCellProps> = ({ count, onOpen }) => {
    if (count === 0) {
        return <td className="accuracy-monitoring-count-zero">0</td>;
    }
    return (
        <td>
            <button type="button" className="accuracy-monitoring-count-button" onClick={onOpen} title="違反の内訳を表示">
                {count.toLocaleString()}
            </button>
        </td>
    );
};

const AccuracyMonitoringPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const toast = useToast();

    const [createdAfter, setCreatedAfter] = useState(searchParams.get('after') || '');
    const [createdBefore, setCreatedBefore] = useState(searchParams.get('before') || '');
    const [selectedGuidelines, setSelectedGuidelines] = useState<string[]>(
        (searchParams.get('guidelines') || '').split(',').filter(Boolean)
    );
    const [chartMode, setChartMode] = useState<'count' | 'rate'>('count');
    const [bucket, setBucket] = useState<'day' | 'week' | 'month'>(
        BUCKET_OPTIONS.some((o) => o.value === searchParams.get('bucket'))
            ? (searchParams.get('bucket') as 'day' | 'week' | 'month')
            : 'month'
    );
    const [divideBy, setDivideBy] = useState<DivideBy>(
        DIVIDE_BY_OPTIONS.some((o) => o.value === searchParams.get('divideBy'))
            ? (searchParams.get('divideBy') as DivideBy)
            : 'none'
    );
    const [chartMetric, setChartMetric] = useState('fp_count');
    const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
    const toggleSeries = (key: string) => {
        setHiddenSeries((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
    const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'created_at');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
        searchParams.get('sortOrder') === 'asc' ? 'asc' : 'desc'
    );

    const toggleSort = (field: string) => {
        if (sortBy === field) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    // Category ids are only unique within one guideline version, so dividing
    // by category only makes sense once the filter has narrowed to exactly one.
    const canDivideByCategory = selectedGuidelines.length === 1;
    const effectiveDivideBy: DivideBy = divideBy === 'category' && !canDivideByCategory ? 'guideline' : divideBy;

    // Keep URL in sync so filters/page survive a refresh.
    useEffect(() => {
        const params: Record<string, string> = {};
        if (createdAfter) params.after = createdAfter;
        if (createdBefore) params.before = createdBefore;
        if (selectedGuidelines.length) params.guidelines = selectedGuidelines.join(',');
        if (bucket !== 'month') params.bucket = bucket;
        if (effectiveDivideBy !== 'none') params.divideBy = effectiveDivideBy;
        if (currentPage > 1) params.page = currentPage.toString();
        if (sortBy !== 'created_at') params.sortBy = sortBy;
        if (sortOrder !== 'desc') params.sortOrder = sortOrder;
        setSearchParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createdAfter, createdBefore, selectedGuidelines, bucket, effectiveDivideBy, currentPage, sortBy, sortOrder]);

    // Reset to page 1 whenever a filter or sort column (not the page itself) changes.
    const filterFingerprint = useMemo(
        () => JSON.stringify({ createdAfter, createdBefore, selectedGuidelines, sortBy, sortOrder }),
        [createdAfter, createdBefore, selectedGuidelines, sortBy, sortOrder]
    );
    const prevFingerprintRef = useRef(filterFingerprint);
    useEffect(() => {
        if (prevFingerprintRef.current !== filterFingerprint) {
            prevFingerprintRef.current = filterFingerprint;
            setCurrentPage(1);
        }
    }, [filterFingerprint]);

    const baseParams = useMemo(
        () => ({
            guidelineId: selectedGuidelines.length ? selectedGuidelines.join(',') : undefined,
            createdAfter: createdAfter || undefined,
            createdBefore: createdBefore || undefined,
        }),
        [selectedGuidelines, createdAfter, createdBefore]
    );

    const timeseriesParams = useMemo(
        () => ({ ...baseParams, bucket, divideBy: effectiveDivideBy }),
        [baseParams, bucket, effectiveDivideBy]
    );
    const { data: timeseriesData, loading: timeseriesLoading } = useAccuracyMonitoringTimeseries(timeseriesParams);
    const { data: jobsData, loading: jobsLoading, error: jobsError } = useAccuracyMonitoringJobs({
        ...baseParams,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        sortBy,
        sortOrder,
    });
    const { data: jobFilterOptionsData } = useJobFilterOptions();

    const openJobDrilldown = (row: AccuracyMonitoringJobRow, countKind: MetricCountKind, count: number) => {
        if (count === 0) return;
        const path = buildMonitoringDrilldownPath({
            jobId: row.job_id,
            jobName: row.job_name || undefined,
            drilldown: {
                categoryId: null,
                categoryName: '全体',
                metricType: 'violation',
                countKind,
            },
        });
        window.open(path, '_blank', 'noopener,noreferrer');
    };

    const [isExporting, setIsExporting] = useState(false);
    const [exportTaskId, setExportTaskId] = useState<string | null>(null);
    const [exportProgress, setExportProgress] = useState(0);

    const downloadMonitoringExportFile = async (taskId: string) => {
        const result: ViolationExportDownloadResponse = await violationsApi.getExportDownload(taskId);
        const blob = await violationsApi.downloadExportFile(result.downloadUrl);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const trackMonitoringExportProgress = (taskId: string) => {
        const pollInterval = setInterval(async () => {
            try {
                const progress: ViolationExportProgressResponse = await violationsApi.getExportProgress(taskId);
                setExportProgress(Math.min(progress.progress.percentage, 100));

                if (progress.status === 'completed') {
                    clearInterval(pollInterval);
                    await downloadMonitoringExportFile(taskId);
                    setIsExporting(false);
                    setExportTaskId(null);
                    setExportProgress(0);
                } else if (progress.status === 'failed' || progress.status === 'canceled') {
                    clearInterval(pollInterval);
                    setIsExporting(false);
                    setExportTaskId(null);
                    setExportProgress(0);
                    if (progress.status === 'failed') {
                        toast.error(progress.errorMessage || 'エクスポートに失敗しました', { title: 'エクスポート失敗' });
                    }
                }
            } catch (error) {
                clearInterval(pollInterval);
                setIsExporting(false);
                setExportTaskId(null);
                setExportProgress(0);
                const errorStatus = (error as any)?.status;
                if (errorStatus !== 404) {
                    toast.error(`エクスポートの進行状況の取得に失敗しました: ${(error as Error).message}`, { title: 'エクスポート失敗' });
                }
            }
        }, 1000);
    };

    const handleExportViolationList = async () => {
        setIsExporting(true);
        setExportProgress(0);
        try {
            const response = await accuracyMonitoringApi.exportViolationList({
                guidelineId: selectedGuidelines.length ? selectedGuidelines.join(',') : undefined,
                createdAfter: createdAfter || undefined,
                createdBefore: createdBefore || undefined,
            });
            if (!response.totalUrls) {
                toast.info('対象の違反がありません。', { title: 'エクスポート' });
                setIsExporting(false);
                return;
            }
            setExportTaskId(response.taskId);
            trackMonitoringExportProgress(response.taskId);
        } catch (error) {
            setIsExporting(false);
            toast.error(`エクスポートの開始に失敗しました: ${(error as Error).message}`, { title: 'エクスポート失敗' });
        }
    };

    const handleCancelExport = async () => {
        if (!exportTaskId) return;
        try {
            await violationsApi.cancelExport(exportTaskId);
        } finally {
            setIsExporting(false);
            setExportTaskId(null);
            setExportProgress(0);
        }
    };

    const chartPoints = useMemo(() => timeseriesData?.points || [], [timeseriesData]);

    // Rate view: each error type as % of violations surviving review (can exceed
    // 100% when a bucket has more false positives than final confirmed violations).
    const chartData = useMemo(
        () =>
            chartPoints.map((p) => {
                const denom = p.violations_after_review_count;
                return {
                    ...p,
                    fp_rate: denom > 0 ? (p.fp_count / denom) * 100 : undefined,
                    fn_rate: denom > 0 ? (p.fn_count / denom) * 100 : undefined,
                    wrong_bounding_box_rate: denom > 0 ? (p.wrong_bounding_box_count / denom) * 100 : undefined,
                };
            }),
        [chartPoints]
    );
    const jobs = jobsData?.items || [];
    const pagination = jobsData?.pagination || null;

    const guidelineOptions = useMemo(() => {
        const guidelines = (jobFilterOptionsData as any)?.data?.guidelines || [];
        return guidelines.map((g: any) => ({ label: g.name || g.id, value: g.id }));
    }, [jobFilterOptionsData]);

    const guidelineNameById = useMemo(() => {
        const map = new Map<string, string>();
        guidelineOptions.forEach((g: { label: string; value: string }) => map.set(g.value, g.label));
        return map;
    }, [guidelineOptions]);

    const categoryNameById = useMemo(() => {
        const map = new Map<string, string>();
        const categories = (jobFilterOptionsData as any)?.data?.categories || [];
        categories.forEach((c: { id: string; title?: string }) => map.set(c.id, c.title || c.id));
        return map;
    }, [jobFilterOptionsData]);

    const isDivided = effectiveDivideBy !== 'none';

    // Reset the legend's hide/show selections when switching what the lines
    // represent (metric keys vs. group ids share the same hiddenSeries set).
    useEffect(() => {
        setHiddenSeries(new Set());
    }, [effectiveDivideBy]);

    const activeMetricOptions = useMemo(() => {
        if (chartMode === 'rate') return DIVIDED_RATE_METRICS;
        return effectiveDivideBy === 'guideline'
            ? [...DIVIDED_COUNT_METRICS, GUIDELINE_ONLY_COUNT_METRIC]
            : DIVIDED_COUNT_METRICS;
    }, [chartMode, effectiveDivideBy]);

    const effectiveChartMetric = activeMetricOptions.some((m) => m.key === chartMetric)
        ? chartMetric
        : activeMetricOptions[0].key;

    // Pivots one row per (bucket, group) into one row per bucket with a
    // numeric column per group, for the selected single metric -- plotting
    // every metric for every group at once would be unreadable.
    const groupedChartData = useMemo(() => {
        if (!isDivided) return { rows: [] as Record<string, unknown>[], groups: [] as { key: string; label: string }[] };

        const rateMetric = DIVIDED_RATE_METRICS.find((m) => m.key === effectiveChartMetric);
        const rowsByBucket = new Map<string, Record<string, unknown>>();
        const groupLabels = new Map<string, string>();
        const groupTotals = new Map<string, number>();

        chartPoints.forEach((p) => {
            const groupKey = p.group_key ?? '__unspecified__';
            if (!groupLabels.has(groupKey)) {
                const fallbackLabel =
                    effectiveDivideBy === 'guideline'
                        ? guidelineNameById.get(groupKey)
                        : categoryNameById.get(groupKey);
                groupLabels.set(groupKey, p.group_label || fallbackLabel || (p.group_key ? groupKey : '未分類'));
            }

            let row = rowsByBucket.get(p.bucket);
            if (!row) {
                row = { bucket: p.bucket };
                rowsByBucket.set(p.bucket, row);
            }

            let value: number | undefined;
            if (rateMetric) {
                const denom = p.violations_after_review_count;
                const numerator = (p as unknown as Record<string, number>)[rateMetric.countKey];
                value = denom > 0 ? (numerator / denom) * 100 : undefined;
            } else {
                value = (p as unknown as Record<string, number>)[effectiveChartMetric];
            }
            row[groupKey] = value;
            groupTotals.set(groupKey, (groupTotals.get(groupKey) || 0) + (value || 0));
        });

        const rows = Array.from(rowsByBucket.values()).sort((a, b) =>
            String(a.bucket).localeCompare(String(b.bucket))
        );
        const groups = Array.from(groupLabels.keys())
            .sort((a, b) => (groupTotals.get(b) || 0) - (groupTotals.get(a) || 0))
            .map((key) => ({ key, label: groupLabels.get(key) || key }));
        return { rows, groups };
    }, [chartPoints, isDivided, effectiveDivideBy, effectiveChartMetric, guidelineNameById, categoryNameById]);

    return (
        <div className="page-container">
            <div className="page-header">
                <h1 className="page-title">精度モニタリング</h1>
                <p className="page-description">
                    レビュー済みURLを対象に、誤検知・検知漏れ・枠ずれの件数と割合を期間・ガイドライン別に確認します。
                </p>
            </div>

            <div className="accuracy-monitoring-chart-section">
                <div className="accuracy-monitoring-chart-header">
                    <div className="accuracy-monitoring-mode-toggle">
                        {BUCKET_OPTIONS.map((o) => (
                            <button
                                key={o.value}
                                type="button"
                                className={`accuracy-monitoring-mode-btn ${bucket === o.value ? 'active' : ''}`}
                                onClick={() => setBucket(o.value)}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                    <div className="accuracy-monitoring-mode-toggle">
                        <button
                            type="button"
                            className={`accuracy-monitoring-mode-btn ${chartMode === 'count' ? 'active' : ''}`}
                            onClick={() => setChartMode('count')}
                        >
                            件数
                        </button>
                        <button
                            type="button"
                            className={`accuracy-monitoring-mode-btn ${chartMode === 'rate' ? 'active' : ''}`}
                            onClick={() => setChartMode('rate')}
                        >
                            割合（%）
                        </button>
                    </div>
                    <div className="accuracy-monitoring-mode-toggle">
                        {DIVIDE_BY_OPTIONS.map((o) => {
                            const disabled = o.value === 'category' && !canDivideByCategory;
                            return (
                                <button
                                    key={o.value}
                                    type="button"
                                    className={`accuracy-monitoring-mode-btn ${effectiveDivideBy === o.value ? 'active' : ''}`}
                                    onClick={() => setDivideBy(o.value)}
                                    disabled={disabled}
                                    title={disabled ? 'ガイドラインを1つだけ選択すると使用できます' : undefined}
                                >
                                    {o.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {isDivided && (
                    <div className="accuracy-monitoring-chart-header accuracy-monitoring-metric-picker-row">
                        <div className="accuracy-monitoring-mode-toggle">
                            {activeMetricOptions.map((m) => (
                                <button
                                    key={m.key}
                                    type="button"
                                    className={`accuracy-monitoring-mode-btn ${effectiveChartMetric === m.key ? 'active' : ''}`}
                                    onClick={() => setChartMetric(m.key)}
                                >
                                    {m.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {timeseriesLoading ? (
                    <div className="loading-state">
                        <Loader2 size={20} className="animate-spin" />
                        <span>読み込み中...</span>
                    </div>
                ) : isDivided ? (
                    <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={groupedChartData.rows} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                            <YAxis
                                tick={{ fontSize: 11 }}
                                tickFormatter={chartMode === 'rate' ? (v: number) => `${v}%` : undefined}
                                label={{
                                    value: activeMetricOptions.find((m) => m.key === effectiveChartMetric)?.name || '',
                                    angle: -90,
                                    position: 'insideLeft',
                                    style: { fontSize: 12 },
                                }}
                            />
                            <Tooltip
                                formatter={
                                    chartMode === 'rate'
                                        ? (value: number | string) => (typeof value === 'number' ? `${value.toFixed(1)}%` : value)
                                        : undefined
                                }
                            />
                            <Legend
                                content={
                                    <div className="accuracy-monitoring-legend">
                                        {groupedChartData.groups.map((g, idx) => {
                                            const isHidden = hiddenSeries.has(g.key);
                                            const color = GROUP_PALETTE[idx % GROUP_PALETTE.length];
                                            return (
                                                <button
                                                    key={g.key}
                                                    type="button"
                                                    className={`accuracy-monitoring-legend-item${isHidden ? ' inactive' : ''}`}
                                                    onClick={() => toggleSeries(g.key)}
                                                >
                                                    <span
                                                        className="accuracy-monitoring-legend-swatch"
                                                        style={{ backgroundColor: isHidden ? INACTIVE_LEGEND_COLOR : color }}
                                                    />
                                                    {g.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                }
                            />
                            {groupedChartData.groups.map((g, idx) => (
                                <Line
                                    key={g.key}
                                    type="monotone"
                                    dataKey={g.key}
                                    name={g.label}
                                    stroke={GROUP_PALETTE[idx % GROUP_PALETTE.length]}
                                    dot={false}
                                    connectNulls
                                    hide={hiddenSeries.has(g.key)}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                ) : chartMode === 'count' ? (
                    <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                            <YAxis
                                yAxisId="counts"
                                tick={{ fontSize: 11 }}
                                label={{ value: '違反件数', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                            />
                            <YAxis
                                yAxisId="urls"
                                orientation="right"
                                tick={{ fontSize: 11 }}
                                label={{ value: 'レビュー済みURL数', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
                            />
                            <Tooltip />
                            <Legend
                                content={
                                    <div className="accuracy-monitoring-legend">
                                        {COUNT_SERIES.map((s) => {
                                            const isHidden = hiddenSeries.has(s.key);
                                            return (
                                                <button
                                                    key={s.key}
                                                    type="button"
                                                    className={`accuracy-monitoring-legend-item${isHidden ? ' inactive' : ''}`}
                                                    onClick={() => toggleSeries(s.key)}
                                                >
                                                    <span
                                                        className="accuracy-monitoring-legend-swatch"
                                                        style={{ backgroundColor: isHidden ? INACTIVE_LEGEND_COLOR : s.color }}
                                                    />
                                                    {s.name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                }
                            />
                            {COUNT_SERIES.map((s) => (
                                <Line
                                    key={s.key}
                                    yAxisId={s.axis}
                                    type="monotone"
                                    dataKey={s.key}
                                    name={s.name}
                                    stroke={s.color}
                                    dot={false}
                                    hide={hiddenSeries.has(s.key)}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                            <YAxis
                                tick={{ fontSize: 11 }}
                                tickFormatter={(v: number) => `${v}%`}
                                label={{ value: 'レビュー後の違反数に対する割合', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                            />
                            <Tooltip formatter={(value: number | string) => (typeof value === 'number' ? `${value.toFixed(1)}%` : value)} />
                            <Legend
                                content={
                                    <div className="accuracy-monitoring-legend">
                                        {RATE_SERIES.map((s) => {
                                            const isHidden = hiddenSeries.has(s.key);
                                            return (
                                                <button
                                                    key={s.key}
                                                    type="button"
                                                    className={`accuracy-monitoring-legend-item${isHidden ? ' inactive' : ''}`}
                                                    onClick={() => toggleSeries(s.key)}
                                                >
                                                    <span
                                                        className="accuracy-monitoring-legend-swatch"
                                                        style={{ backgroundColor: isHidden ? INACTIVE_LEGEND_COLOR : s.color }}
                                                    />
                                                    {s.name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                }
                            />
                            {RATE_SERIES.map((s) => (
                                <Line
                                    key={s.key}
                                    type="monotone"
                                    dataKey={s.key}
                                    name={s.name}
                                    stroke={s.color}
                                    dot={false}
                                    connectNulls
                                    hide={hiddenSeries.has(s.key)}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>

            <div className="filters-section">
                <div className="filters-row">
                    <div className="filter-controls">
                        <div className="multi-select-filter accuracy-monitoring-date-filter">
                            <label className="filter-label">
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Calendar size={12} />
                                    期間（ジョブ作成日時）
                                </span>
                            </label>
                            <div className="accuracy-monitoring-date-range">
                                <input
                                    type="date"
                                    value={formatDateForInput(createdAfter)}
                                    onChange={(e) => setCreatedAfter(e.target.value)}
                                    className="accuracy-monitoring-date-input"
                                />
                                <span className="accuracy-monitoring-date-separator">〜</span>
                                <input
                                    type="date"
                                    value={formatDateForInput(createdBefore)}
                                    onChange={(e) => setCreatedBefore(e.target.value)}
                                    className="accuracy-monitoring-date-input"
                                />
                            </div>
                        </div>

                        <MultiSelectFilter
                            label={<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FileText size={12} />ガイドライン</span>}
                            options={guidelineOptions}
                            selectedValues={selectedGuidelines}
                            onSelectionChange={setSelectedGuidelines}
                            placeholder="すべてのガイドライン"
                        />
                    </div>

                    <div className="accuracy-monitoring-export">
                        {isExporting ? (
                            <div className="accuracy-monitoring-export-progress">
                                <Loader2 size={14} className="animate-spin" />
                                <span>エクスポート中... {exportProgress}%</span>
                                <button type="button" className="accuracy-monitoring-mode-btn" onClick={handleCancelExport}>
                                    キャンセル
                                </button>
                            </div>
                        ) : (
                            <div className="accuracy-monitoring-mode-toggle">
                                <button
                                    type="button"
                                    className="accuracy-monitoring-mode-btn accuracy-monitoring-export-btn"
                                    onClick={handleExportViolationList}
                                >
                                    <Download size={14} />
                                    違反一覧をExcelでエクスポート
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="accuracy-monitoring-results">
                {jobsError && (
                    <div className="error-state">
                        <AlertTriangle size={20} />
                        <span>{jobsError}</span>
                    </div>
                )}

                {jobsLoading ? (
                    <div className="loading-state">
                        <Loader2 size={20} className="animate-spin" />
                        <span>読み込み中...</span>
                    </div>
                ) : (
                    <>
                        <div className="accuracy-monitoring-table-wrap">
                            <table className="accuracy-monitoring-table">
                                <thead>
                                    <tr>
                                        <SortableTh field="created_at" label="作成日時" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <th>ジョブ名</th>
                                        <th>ガイドライン</th>
                                        <SortableTh field="reviewed_url_count" label="レビュー済みURL数" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <SortableTh field="fp_count" label="誤検知（FP）" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <SortableTh field="fn_count" label="検知漏れ（FN）" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <SortableTh field="wrong_bounding_box_count" label="枠ずれ" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                        <SortableTh field="violations_after_review_count" label="レビュー後の違反数" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {jobs.map((row) => (
                                        <tr key={row.job_id} className="accuracy-monitoring-row">
                                            <td>{row.created_at ? new Date(row.created_at).toLocaleString('ja-JP') : '-'}</td>
                                            <td>{row.job_name || '-'}</td>
                                            <td>{guidelineNameById.get(row.guideline_id) || row.guideline_id}</td>
                                            <td>{row.reviewed_url_count.toLocaleString()}</td>
                                            <MonitoringCountCell count={row.fp_count} onOpen={() => openJobDrilldown(row, 'fp', row.fp_count)} />
                                            <MonitoringCountCell count={row.fn_count} onOpen={() => openJobDrilldown(row, 'fn', row.fn_count)} />
                                            <MonitoringCountCell count={row.wrong_bounding_box_count} onOpen={() => openJobDrilldown(row, 'wb', row.wrong_bounding_box_count)} />
                                            <td>{row.violations_after_review_count.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                    {jobs.length === 0 && (
                                        <tr>
                                            <td colSpan={8} className="accuracy-monitoring-empty">該当するデータがありません</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {pagination && pagination.total_pages > 1 && (
                            <div className="pagination">
                                <div className="pagination-info">
                                    {pagination.total_items}件中 {((pagination.current_page - 1) * pagination.items_per_page) + 1}
                                    -{Math.min(pagination.current_page * pagination.items_per_page, pagination.total_items)}件を表示
                                </div>
                                <div className="pagination-controls">
                                    <button
                                        className="pagination-btn"
                                        onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                        disabled={!pagination.has_previous}
                                    >
                                        <ChevronLeft size={16} />前へ
                                    </button>
                                    <div className="pagination-numbers">
                                        {getPageNumbers(pagination.current_page, pagination.total_pages).map((p, idx) =>
                                            p === '...' ? (
                                                <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
                                            ) : (
                                                <button
                                                    key={p}
                                                    className={`pagination-number ${pagination.current_page === p ? 'active' : ''}`}
                                                    onClick={() => setCurrentPage(p)}
                                                >
                                                    {p}
                                                </button>
                                            )
                                        )}
                                    </div>
                                    <button
                                        className="pagination-btn"
                                        onClick={() => setCurrentPage((p) => Math.min(p + 1, pagination.total_pages))}
                                        disabled={!pagination.has_next}
                                    >
                                        次へ<ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AccuracyMonitoringPage;
